import { TextField } from "@mui/material";
import { NumberFormFieldProps } from "../types";
import { memo, useState, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";

const NumberFormField = memo(({ name, label, disabled = false, required, props }: NumberFormFieldProps) => {
  const { onChange, value, ...textFieldProps } = props;

  const [localValue, setLocalValue] = useState<string | number | null>(value ?? "");

  // Debounced function to call the parent's onChange handler
  const debouncedOnChange = useMemo(
    () =>
      debounce((event) => {
        onChange(event);
      }, 300),
    [onChange],
  );

  const handleInput = (event: any) => {
    const value = event.target.value;

    // Replace all non-numeric characters except for the decimal point.
    const cleanedValue = value.replace(/[^0-9.]/g, "");

    // Ensure that there's only one decimal point in the value.
    const parts = cleanedValue.split(".");
    if (parts.length > 2) {
      event.target.value = parts[0] + "." + parts.slice(1).join("");
    } else {
      event.target.value = cleanedValue;
    }
  };

  const handleChange = (e: any) => {
    const newValue = e.target.value;
    setLocalValue(newValue); // Update local state immediately
    debouncedOnChange(e); // Trigger the parent's onChange after debounce
  };

  // Sync local state with the parent value when the parent changes
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value ?? "");
    }
  }, [value]);

  return (
    <TextField
      fullWidth
      id={name}
      name={name}
      disabled={disabled}
      label={label}
      required={required}
      onInput={handleInput}
      value={localValue}
      onChange={handleChange}
      inputProps={{
        ...(props.inputProps || {}),
        inputMode: "decimal",
        pattern: "[0-9.]*",
      }}
      {...textFieldProps}
    />
  );
});

export default NumberFormField;
